import { Channel, EventTeam, Team } from '@domain/eventTeam';

export type ValidationRule = 'negative' | 'over-capacity' | 'over-team-limit' | 'over-channel-capacity';

export interface ValidationError {
  teamId?: string;
  channelId?: string;
  rule: ValidationRule;
  message?: string;
}

const isPositiveCapacity = (capacity: number | null | undefined): capacity is number =>
  capacity != null && capacity > 0;

// Mirrors the legacy semantic of `team.ticketSalesClosed`, which was bubbled up
// from the children whenever every channel had its toggle off. The team itself
// has no dedicated flag in our model, so we derive the state from the channels.
// Empty channel lists never qualify as closed (matches the legacy bubble-up
// behaviour, which only marked the team closed once it had seen all channels
// toggle off).
export const isTeamSalesClosed = (team: Team): boolean =>
  team.channels.length > 0 && team.channels.every((channel) => channel.isTicketSalesClosed);

// Visits every channel in the value (nested under a team first, then standalone)
// in the same order the validators used inline. Centralising the traversal keeps
// cognitive complexity of each validator low and guarantees consistent ordering
// across rules.
const forEachChannel = (value: EventTeam, visit: (channel: Channel) => void): void => {
  for (const team of value.teams) {
    for (const channel of team.channels) visit(channel);
  }
  for (const channel of value.channelsWithoutTeams) visit(channel);
};

export const validateNegative = (value: EventTeam): ValidationError[] => {
  const errors: ValidationError[] = [];
  for (const team of value.teams) {
    if (isTeamSalesClosed(team)) continue;
    if (team.maxTicketQuantity !== null && team.maxTicketQuantity <= 0) {
      errors.push({ teamId: team.id, rule: 'negative' });
    }
  }
  forEachChannel(value, (channel) => {
    if (channel.isTicketSalesClosed) return;
    if (channel.maxTicketQuantity !== null && channel.maxTicketQuantity <= 0) {
      errors.push({ channelId: channel.channel.id, rule: 'negative' });
    }
  });
  return errors;
};

export const validateOverCapacity = (value: EventTeam, capacity: number | null | undefined): ValidationError[] => {
  if (!isPositiveCapacity(capacity)) return [];
  const errors: ValidationError[] = [];
  for (const team of value.teams) {
    if (isTeamSalesClosed(team)) continue;
    if (team.maxTicketQuantity !== null && team.maxTicketQuantity > capacity) {
      errors.push({ teamId: team.id, rule: 'over-capacity' });
    }
  }
  return errors;
};

export const validateOverTeamLimit = (value: EventTeam): ValidationError[] => {
  const errors: ValidationError[] = [];
  for (const team of value.teams) {
    if (team.maxTicketQuantity === null) continue;
    for (const channel of team.channels) {
      if (channel.isTicketSalesClosed) continue;
      if (channel.maxTicketQuantity !== null && channel.maxTicketQuantity > team.maxTicketQuantity) {
        errors.push({ channelId: channel.channel.id, rule: 'over-team-limit' });
      }
    }
  }
  return errors;
};

export const validateOverChannelCapacity = (
  value: EventTeam,
  capacity: number | null | undefined
): ValidationError[] => {
  if (!isPositiveCapacity(capacity)) return [];
  const errors: ValidationError[] = [];
  forEachChannel(value, (channel) => {
    if (channel.isTicketSalesClosed) return;
    if (channel.maxTicketQuantity !== null && channel.maxTicketQuantity > capacity) {
      errors.push({ channelId: channel.channel.id, rule: 'over-channel-capacity' });
    }
  });
  return errors;
};

export const validateTeamChannels = (value: EventTeam, capacity: number | null | undefined): ValidationError[] => [
  ...validateNegative(value),
  ...validateOverCapacity(value, capacity),
  ...validateOverTeamLimit(value),
  ...validateOverChannelCapacity(value, capacity),
];
